package org.multiplatform.kotfolio.pages.flutter

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties

@Page("/flutter/plugin")
@Composable
fun PluginPage() {
    BlogLayout(
        PageProperties(
            "Channel Communication",
            "Native code integration is done using platform channels, which allow communication between Dart and native code (Kotlin/Java for Android, Swift/Objective-C for iOS). Method channels are used to send and receive messages, enabling the invocation of platform-specific APIs and functionality."
        ),
        BlogProperties("Flutter", "Plugin (Channel Communication)")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "How do you integrate native code (Android/iOS) with Flutter?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Native code integration is done using platform channels, which allow communication between Dart and native code (Kotlin/Java for Android, Swift/Objective-C for iOS). Method channels are used to send and receive messages, enabling the invocation of platform-specific APIs and functionality.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "What are Platform Channels?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Platform channels are essentially a communication channel that allows your Flutter code to interact with native code written in Java/Kotlin (Android) or Swift/Objective-C (iOS). This enables you to leverage platform-specific features directly within your Flutter app, maintaining a consistent user experience across platforms.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "MethodChannel",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "MethodChannel is used to invoke native functions from Flutter and receive a single response. It follows a request response model.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "MethodChannel Work",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Flutter sends a request using invokeMethod().",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Native code receives the request, processes it, and returns a response.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Flutter receives the result and updates the UI accordingly.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "EventChannel",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "EventChannel is used for streaming real-time data from native code to Flutter. Unlike MethodChannel, which handles one-time requests, EventChannel provides a continuous flow of data through a Dart Stream.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "EventChannel Work",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Flutter subscribes to an EventChannel.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Native code streams data continuously (sensor updates, location tracking).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Flutter receives updates whenever the data changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    import 'package:flutter/material.dart';
                    import 'package:flutter/services.dart';
                    import 'package:provider/provider.dart';

                    void main() {
                      runApp(
                        const MyApp(),
                      );
                    }

                    class MyApp extends StatelessWidget {
                      const MyApp({Key? key}) : super(key: key);

                      @override
                      Widget build(BuildContext context) {
                        return const MaterialApp(
                          home: MyHomePage(),
                        );
                      }
                    }

                    class MyHomePage extends StatefulWidget {
                      const MyHomePage({Key? key}) : super(key: key);

                      @override
                      State<MyHomePage> createState() => _MyHomePageState();
                    }

                    class _MyHomePageState extends State<MyHomePage> {
                      static const platform = MethodChannel('multiplatform.developer/native');
                      String msgFromNative = '';

                      Future<void> _passDataToNative(String userName) async {
                        try {
                          var result = await platform.invokeMethod('passDataToNative', [
                            {"text": userName}
                          ]);
                          msgFromNative = result;
                        } on PlatformException catch (e) {
                          msgFromNative = e.message;
                        }
                        setState(() {
                          msgFromNative = msgFromNative;
                        });
                      }

                      @override
                      Widget build(BuildContext context) {
                        return Scaffold(
                          appBar: AppBar(
                            title: const Text('Example'),
                          ),
                          body: Center(
                            child: Column(
                              mainAxisSize: MainAxisSize.min,
                              mainAxisAlignment: MainAxisAlignment.center,
                              children: <Widget>[
                                ElevatedButton(
                                  onPressed: () {
                                      _passDataToNative('Flutter Dev');
                                  },
                                  child: const Text('Pass data to native'),
                                ),
                                Text(msgFromNative),
                              ],
                            ),
                          ),
                        );
                      }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    class MainActivity: FlutterActivity() {
                        private val channel = "multiplatform.developer/native"

                        override fun configureFlutterEngine(@NonNull flutterEngine: FlutterEngine) {
                            super.configureFlutterEngine(flutterEngine)
                            MethodChannel(flutterEngine.dartExecutor.binaryMessenger, channel).setMethodCallHandler {
                                // This method is invoked on the main thread.
                                    call, result ->

                                if (call.method == "passDataToNative") {
                                    // Your code here
                                    val arguments = call.arguments as List<Map<String, Any>>
                                    val userName = arguments.first()["text"] as String
                                    result.success(userName)
                                   
                                } else {
                                    result.notImplemented()
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    import UIKit
                    import Flutter

                    @UIApplicationMain
                    @objc class AppDelegate: FlutterAppDelegate {
                        override func application(
                        _ application: UIApplication,
                        didFinishLaunchingWithOptions launchOptions: [UIApplication.LaunchOptionsKey: Any]?
                        ) -> Bool {
                            let controller : FlutterViewController = window?.rootViewController as! FlutterViewController
                            let batteryChannel = FlutterMethodChannel(name: "multiplatform.developer/native",
                            binaryMessenger: controller.binaryMessenger)
                            batteryChannel.setMethodCallHandler({
                                [weak self] (call: FlutterMethodCall, result: FlutterResult) -> Void in
                                // This method is invoked on the UI thread.
                                guard call.method == "passDataToNative"
                                else {
                                result(FlutterMethodNotImplemented)
                                return
                            }
                                self?.passDataToFlutter(result: result,call:call)
                            })
                            GeneratedPluginRegistrant.register(with: self)
                            return super.application(application, didFinishLaunchingWithOptions: launchOptions)
                        }
                        
                        private func passDataToFlutter(result: FlutterResult, call: FlutterMethodCall) {
                            guard let arguments = call.arguments as? [[String: Any]],
                            let firstArgument = arguments.first,
                            let userName = firstArgument["text"] as? String else {
                                result(FlutterError(code: "INVALID_ARGUMENT", message: "Invalid arguments", details: nil))
                                return
                            }
                            // Print the userName
                            print("Received userName: \(userName)")
                            // Send the result back to Flutter
                            result("Received userName: \(userName)")
                        }
                    }
                    """.trimIndent()
            )
        }
    }
}

//https://github.com/zainulabdn/portfolio-app-flutter (Flip View)